import React, { useState } from "react";
import { Link } from "react-router-dom";
import Cloud9MatrixModal from "./Cloud9MatrixModal";
import "./AdminTools.css";

const AdminTools = ({ adminId, adminGroup }) => {
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [orphaned, setOrphaned] = useState(null);
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [selectedOrphans, setSelectedOrphans] = useState([]);
  const [deletingOrphans, setDeletingOrphans] = useState(false);
  const [searchUsername, setSearchUsername] = useState("");
  const [searchResult, setSearchResult] = useState(null);

  // Find User Posts / Admin Boost
  const [postSearchUsername, setPostSearchUsername] = useState("");
  const [userPosts, setUserPosts] = useState([]);
  const [loadingUserPosts, setLoadingUserPosts] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState({});

  //Password Reset
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [resetPassword, setResetPassword] = useState("");

  // Moderation
  const [mutedUsers, setMutedUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [showMutedModal, setShowMutedModal] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  // Read-only administrative moderation reports
  const [adminBlocksReport, setAdminBlocksReport] = useState(null);
  const [warningHistoryReport, setWarningHistoryReport] = useState(null);
  const [moderationReportLoading, setModerationReportLoading] = useState(false);

  // Reports
  const [reports, setReports] = useState([]);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportPostDetail, setReportPostDetail] = useState(null);
  const [showReportPostModal, setShowReportPostModal] = useState(false);

    // View Analytics Summary
  const [viewSummary, setViewSummary] = useState(null);
  const [showViewSummaryModal, setShowViewSummaryModal] = useState(false);
  const [loadingViewSummary, setLoadingViewSummary] = useState(false);

  // V9.7.0 Registration Analytics
  const [registrationAnalytics, setRegistrationAnalytics] = useState(null);
  const [showRegistrationAnalytics, setShowRegistrationAnalytics] = useState(false);
  const [loadingRegistrationAnalytics, setLoadingRegistrationAnalytics] = useState(false);
  const [registrationFilter, setRegistrationFilter] = useState("");
  const [registrationStatus, setRegistrationStatus] = useState("ALL");

  // User Login Analytics
  const [loginSearch, setLoginSearch] = useState("");
  const [loginStatus, setLoginStatus] = useState("ALL");
  const [loginCountry, setLoginCountry] = useState("");
  const [loginFrom, setLoginFrom] = useState("");
  const [loginTo, setLoginTo] = useState("");
  const [loginPage, setLoginPage] = useState(1);
  const [loginJumpPage, setLoginJumpPage] = useState("");
  const [userLoginAnalytics, setUserLoginAnalytics] = useState(null);
  const [showUserLoginAnalyticsModal, setShowUserLoginAnalyticsModal] =
    useState(false);
  const [loadingUserLoginAnalytics, setLoadingUserLoginAnalytics] =
    useState(false);
  if (!adminId || adminGroup !== 1) return <p>❌ Unauthorized Access</p>;

  // ===================================================
  // 🔧 SYSTEM + USER HANDLERS
  // ===================================================
  const handleBackupRestore = async () => {
    const choice = window.prompt("Type 'backup' or 'restore':");
    if (!choice) return;
    const route =
      choice.toLowerCase() === "backup"
        ? `${process.env.REACT_APP_API_URL}/admin/backup`
        : choice.toLowerCase() === "restore"
        ? `${process.env.REACT_APP_API_URL}/admin/restore`
        : null;
    if (!route) return alert("❌ Invalid input.");
    try {
      const res = await fetch(route);
      const data = await res.json();
      alert(data.message || "✅ Operation complete");
    } catch {
      alert("❌ Could not contact admin service.");
    }
  };

  const handleViewAnalyticsSummary = async () => {
    setLoadingViewSummary(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/view-analytics-summary`,
        {
          credentials: "include",
        }
      );

      const data = await res.json();

      if (!data.success) {
        alert(`❌ ${data.error || "Failed to load analytics summary."}`);
        return;
      }

      setViewSummary(data);
      setShowViewSummaryModal(true);
    } catch {
      alert("❌ Could not contact analytics summary service.");
    } finally {
      setLoadingViewSummary(false);
    }
  };

  const handleUserLoginAnalytics = async (page = 1) => {
    setLoadingUserLoginAnalytics(true);
    try {
      const params = new URLSearchParams({ page: String(page), search: loginSearch,
        status: loginStatus, from: loginFrom, to: loginTo, country: loginCountry });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/user-login-analytics?${params}`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(`❌ ${data.error || "Failed to load user login analytics."}`);
        return;
      }
      setUserLoginAnalytics(data);
      setLoginPage(page);
      setShowUserLoginAnalyticsModal(true);
    } catch {
      alert("❌ Could not contact user login analytics service.");
    } finally { setLoadingUserLoginAnalytics(false); }
  };

  const handleRegistrationAnalytics = async () => {
    setLoadingRegistrationAnalytics(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/registration-analytics`, {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(`❌ ${data.error || "Unable to load registration analytics."}`);
        return;
      }
      setRegistrationAnalytics(data);
      setShowRegistrationAnalytics(true);
    } catch (err) {
      alert("❌ Could not contact registration analytics service.");
    } finally {
      setLoadingRegistrationAnalytics(false);
    }
  };

  const handleCleanupViewAnalytics = async () => {
    if (
      !window.confirm(
        "Delete view analytics records older than 90 days?\n\nThis will NOT change posts.view_count totals."
      )
    )
      return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/cleanup-view-analytics`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const data = await res.json();

      if (data.success) {
        if ((data.deletedRows ?? 0) > 0) {
          alert(
            `✅ View analytics cleanup complete.\nDeleted rows: ${data.deletedRows}`
          );
        } else {
          alert("✅ No old view analytics to clean.\nDeleted rows: 0");
        }
      } else {
        alert(`❌ ${data.error || "Cleanup failed."}`);
      }
    } catch {
      alert("❌ Could not contact cleanup service.");
    }
  };

  const handleSyncFollowTables = async () => {
    if (!window.confirm("Sync followers/followings tables?")) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/sync-follow-tables`,
        { method: "POST" }
      );
      const data = await res.json();
      alert(
        data.success
          ? `✅ Synced successfully.\nAdded to followers: ${data.followersAdded}\nAdded to followings: ${data.followingsAdded}`
          : `❌ Sync failed: ${data.error}`
      );
    } catch {
      alert("❌ Could not contact the sync service.");
    }
  };

  const handleCheckOrphanedUploads = async () => {
    setLoadingOrphans(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/orphaned-uploads`,
        { credentials: "include" }
      );
      const data = await res.json();
      setOrphaned(data);
      setSelectedOrphans([]);
    } catch {
      setOrphaned({ error: "Failed to check uploads." });
      setSelectedOrphans([]);
    } finally {
      setLoadingOrphans(false);
    }
  };

  const toggleOrphanSelection = (file) => {
    setSelectedOrphans((current) =>
      current.includes(file)
        ? current.filter((item) => item !== file)
        : [...current, file]
    );
  };

  const selectAllOrphans = () => {
    setSelectedOrphans(
      Array.isArray(orphaned?.orphaned) ? [...orphaned.orphaned] : []
    );
  };

  const clearOrphanSelection = () => {
    setSelectedOrphans([]);
  };

  const handleDeleteSelectedOrphans = async () => {
    if (selectedOrphans.length === 0) {
      alert("Select at least one orphaned file first.");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedOrphans.length} selected orphaned file(s)?\n\nThis cannot be undone.`
    );

    if (!confirmed) return;

    setDeletingOrphans(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/orphaned-uploads/delete`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filenames: selectedOrphans }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(`❌ ${data.error || "Unable to delete selected files."}`);
        return;
      }

      const deletedCount = Array.isArray(data.deleted) ? data.deleted.length : 0;
      const skippedCount = Array.isArray(data.skipped) ? data.skipped.length : 0;
      const failedCount = Array.isArray(data.failed) ? data.failed.length : 0;

      alert(
        `Cleanup complete.\nDeleted: ${deletedCount}\nSkipped: ${skippedCount}\nFailed: ${failedCount}`
      );

      await handleCheckOrphanedUploads();
    } catch {
      alert("❌ Could not contact the orphan cleanup service.");
    } finally {
      setDeletingOrphans(false);
    }
  };

  const handleAdminBoost = async () => {
    const postId = prompt("Enter Post ID to boost:");
    if (!postId) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/posts/${postId}/boost`,
        { method: "POST", credentials: "include" }
      );
      const data = await res.json();
      alert(data.success ? "✅ Boosted successfully." : `❌ ${data.error}`);
    } catch {
      alert("❌ Could not contact boost service.");
    }
  };

  const handleSearchUserPosts = async () => {
    const cleanUsername = postSearchUsername.trim();

    if (!cleanUsername) {
      alert("❌ Enter a username first.");
      setUserPosts([]);
      return;
    }

    setLoadingUserPosts(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user/${cleanUsername}/posts`,
        { credentials: "include" }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(`❌ ${data.error || "Unable to load posts."}`);
        setUserPosts([]);
        return;
      }

      setUserPosts(Array.isArray(data) ? data : []);
      setExpandedPosts({});
    } catch {
      alert("❌ Could not contact post search service.");
      setUserPosts([]);
      setExpandedPosts({});
    } finally {
      setLoadingUserPosts(false);
    }
  };

  const handleBoostFoundPost = async (postId) => {
    if (!postId) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/posts/${postId}/boost`,
        { method: "POST", credentials: "include" }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(`❌ ${data.error || data.message || "Unable to boost post."}`);
        return;
      }

      alert(data.message || "✅ Post boosted successfully.");

      setUserPosts((prev) =>
        prev.map((post) =>
          Number(post.id) === Number(postId)
            ? { ...post, isBoosted: true }
            : post
        )
      );
    } catch {
      alert("❌ Could not contact boost service.");
    }
  };

  const togglePostExpanded = (postId) => {
    setExpandedPosts((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  const handleBoostQueueRotation = async () => {
    if (!window.confirm("Rotate boost queue?")) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/boost/rotate`,
        { method: "PATCH", credentials: "include" }
      );

      const data = await res.json();
      alert(data.success ? `✅ ${data.message}` : `❌ ${data.message}`);
    } catch {
      alert("❌ Could not contact boost queue service.");
    }
  };

  const handleAdminDeleteUser = async () => {
    const userId = prompt("Enter User ID to delete:");
    if (!userId) return;
    if (!window.confirm(`Delete user ID ${userId}?`)) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${userId}`,
        { method: "DELETE", credentials: "include" }
      );
      const data = await res.json();
      alert(data.success ? "✅ User deleted." : `❌ ${data.error}`);
    } catch {
      alert("❌ Could not contact delete service.");
    }
  };

  const handleAdminUnblockUser = async () => {
    const userId = prompt("Enter blocked User ID to reinstate:");
    if (!userId) return;
    if (!window.confirm(`Reinstate user ID ${userId}?`)) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/unblock`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      alert(data.ok ? "✅ User reinstated." : `❌ ${data.error}`);
    } catch {
      alert("❌ Could not contact unblock service.");
    }
  };

  const handleSearchUser = async () => {
    if (!searchUsername) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/user/${searchUsername}`
      );
      const data = await res.json();
      if (data.success) setSearchResult(data.user);
      else alert(`❌ ${data.error}`);
    } catch {
      alert("❌ Could not contact search service.");
    }
  };

  // ===================================================
  // 🧩 MODERATION HANDLERS (Mutes, Blocks, Reports)
  // ===================================================

  const fetchMutedUsers = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/mutes`);
      const data = await res.json();
      setMutedUsers(data);
      setShowMutedModal(true);
    } catch {
      alert("❌ Failed to load muted users.");
    }
  };

  const removeMute = async (id) => {
    if (!window.confirm("Remove this mute?")) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/mute/remove/${id}`
      );
      const data = await res.json();
      if (data.success)
        setMutedUsers((prev) => prev.filter((m) => m.id !== id));
      alert(data.message || "✅ Mute removed.");
    } catch {
      alert("❌ Network error removing mute.");
    }
  };

  const fetchBlockedUsers = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/blocks`);
      const data = await res.json();
      setBlockedUsers(data);
      setShowBlockedModal(true);
    } catch {
      alert("❌ Failed to load blocked users.");
    }
  };

  const removeBlock = async (id) => {
    if (!window.confirm("Remove this block?")) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/block/remove/${id}`
      );
      const data = await res.json();
      if (data.success)
        setBlockedUsers((prev) => prev.filter((b) => b.id !== id));
      alert(data.message || "✅ Block removed.");
    } catch {
      alert("❌ Network error removing block.");
    }
  };

  const loadModerationReport = async (kind) => {
    setModerationReportLoading(true);
    try {
      const endpoint = kind === "blocks" ? "administrative-blocks" : "warning-history";
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/${endpoint}`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Unable to load report.");
      if (kind === "blocks") setAdminBlocksReport(data.records);
      else setWarningHistoryReport(data.records);
    } catch (err) { alert(`❌ ${err.message}`); }
    finally { setModerationReportLoading(false); }
  };

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/reports`);
      const data = await res.json();
      setReports(data);
      setShowReportsModal(true);
    } catch {
      alert("❌ Failed to load reports.");
    } finally {
      setLoadingReports(false);
    }
  };

  const updateReportStatus = async (id, status) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/report/${id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      const data = await res.json();
      if (data.success)
        setReports((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status } : r))
        );
    } catch {
      alert("❌ Failed to update report status.");
    }
  };

  // ===================================================
  // 🧾 VIEW REPORTED POST DETAIL
  // ===================================================

  const viewReportedPost = async (postId, report = null) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/post/${postId}`,
        { credentials: "include" }
      );

      const data = await res.json();

      if (!data || data.error) {
        alert("❌ Failed to load post content.");
        return;
      }

      setReportPostDetail({
        ...data,
        reportContext: report,
      });
      setShowReportPostModal(true);
    } catch (err) {
      console.error("❌ Report post detail error:", err);
      alert("❌ Could not load post content.");
    }
  };

  // ===================================================
  const cloud9Groups = [
    {
      id: 5,
      name: "Basic",
      description: "Free basic account",
      subscriptionFee: 0,
      postLimit: 280,
      mediaLimit: 0,
      editTimeout: 0,
      priorityBoost: false,
      isAvailable: true,
    },
    {
      id: 6,
      name: "Silver",
      description: "Entry-level subscription plan",
      subscriptionFee: 4.99,
      postLimit: 500,
      mediaLimit: 4096,
      editTimeout: 30,
      priorityBoost: true,
      paypalPlanId: "P-0JH73683AW042032LNKXQDLQ",
      isAvailable: true,
    },
    {
      id: 7,
      name: "Gold",
      description: "Mid-tier subscription plan",
      subscriptionFee: 7.99,
      postLimit: 1000,
      mediaLimit: 8192,
      editTimeout: 60,
      priorityBoost: true,
      paypalPlanId: "P-8FS09903SP9316003NKXQKSQ",
      isAvailable: true,
    },
    {
      id: 8,
      name: "Platinum",
      description: "High-tier plan with boosts",
      subscriptionFee: 11.99,
      postLimit: 2000,
      mediaLimit: 16384,
      editTimeout: 120,
      priorityBoost: true,
      paypalPlanId: "P-01M5338393508891GNKXQLRI",
      isAvailable: true,
    },
    {
      id: 9,
      name: "Diamond",
      description: "Top-tier premium plan",
      subscriptionFee: 19.99,
      postLimit: 3000,
      mediaLimit: 20480,
      editTimeout: 180,
      priorityBoost: true,
      paypalPlanId: "P-2HT74804AM390580CNKXQMYI",
      isAvailable: true,
    },
  ];

  // ===================================================
  // RENDER
  // ===================================================
  return (
    <div className="admin-dashboard">
      <h1 className="admin-dashboard-header">Scoldy Admin Dashboard</h1>

      {/* SYSTEM */}
      <div className="admin-section">
        <h3>System Maintenance</h3>
        <div className="admin-grid">
  <button onClick={handleViewAnalyticsSummary}>
    📊 View Analytics Summary
  </button>

  <button
    onClick={() => handleUserLoginAnalytics(1)}
    disabled={loadingUserLoginAnalytics}
  >
    👤 User Login Analytics
  </button>

  <button onClick={handleRegistrationAnalytics} disabled={loadingRegistrationAnalytics}>
    📝 Registration Analytics
  </button>

  <button onClick={handleCleanupViewAnalytics}>
    🧹 Clean View Analytics
  </button>

  <button onClick={handleSyncFollowTables}>
    🔄 Sync Follow Tables
  </button>

  <button onClick={handleCheckOrphanedUploads}>
    🧹 Check Orphans
  </button>
</div>
      </div>

      {/* USER MGMT */}
      <div className="admin-section">
        <h3>User Management</h3>
        <div className="admin-grid">
          <button onClick={() => setShowResetModal(true)}>
            🔐 Reset Password
          </button>

          <button onClick={handleAdminDeleteUser}>🗑️ Delete User</button>
          <button onClick={handleAdminUnblockUser}>♻️ Reinstate User</button>
          <button onClick={fetchMutedUsers}>👥 View Muted Users</button>
          <button onClick={fetchBlockedUsers}>🚫 View Blocked Users</button>
          <button onClick={fetchReports}>⚠️ View Reports</button>
          <button disabled={moderationReportLoading} onClick={() => loadModerationReport("blocks")}>Administrative Blocks</button>
          <button disabled={moderationReportLoading} onClick={() => loadModerationReport("warnings")}>Warning History</button>
        </div>

        <div className="admin-search">
          <input
            type="text"
            placeholder="Search username..."
            value={searchUsername}
            onChange={(e) => setSearchUsername(e.target.value)}
          />
          <button onClick={handleSearchUser}>🔎 Find User</button>
        </div>

        {searchResult && (
          <div className="admin-card">
            <h4>🔍 User Found</h4>

            <div
              style={{
                display: "flex",
                gap: "14px",
                alignItems: "center",
                marginTop: "10px",
                marginBottom: "12px",
              }}
            >
              <img
                src={
                  searchResult.thumbnail
                    ? `https://highroad-001-site2.mtempurl.com/backend${searchResult.thumbnail}`
                    : "/images/default-thumbnail.jpg"
                }
                alt={searchResult.username || "User"}
                style={{
                  width: "72px",
                  height: "72px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #ddd",
                  backgroundColor: "#f3f4f6",
                }}
              />

              <div>
                <div style={{ fontSize: "18px", fontWeight: 700 }}>
                  {searchResult.name || searchResult.username}
                </div>

                <div style={{ color: "#555" }}>@{searchResult.username}</div>

                <div
                  style={{
                    color: "#777",
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  {searchResult.email}
                </div>
              </div>
            </div>

            <p>
              <strong>ID:</strong> {searchResult.id}
            </p>

            <p>
              <strong>Current Group:</strong> {searchResult.group_id}
            </p>
                              <div
              style={{
                marginTop: "16px",
                marginBottom: "16px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: "12px" }}>
                Login Audit Information
              </h4>

              {searchResult.audit?.lastSuccessfulLogin ? (
                <>
                  <p>
                    <strong>Last Successful Login:</strong>{" "}
                    {new Date(
                      searchResult.audit.lastSuccessfulLogin.createdAt
                    ).toLocaleString()}
                  </p>

                  <p>
                    <strong>Last Login IP:</strong>{" "}
                    {searchResult.audit.lastSuccessfulLogin.ip_address ||
                      "Not available"}
                  </p>

                  {searchResult.audit.lastSuccessfulLogin.forwarded_ip &&
                    searchResult.audit.lastSuccessfulLogin.forwarded_ip !==
                      searchResult.audit.lastSuccessfulLogin.ip_address && (
                      <p>
                        <strong>Forwarded IP:</strong>{" "}
                        {searchResult.audit.lastSuccessfulLogin.forwarded_ip}
                      </p>
                    )}
                </>
              ) : (
                <p>No successful login audit found.</p>
              )}

              {searchResult.audit?.lastFailedLogin ? (
                <>
                  <p>
                    <strong>Last Failed Login:</strong>{" "}
                    {new Date(
                      searchResult.audit.lastFailedLogin.createdAt
                    ).toLocaleString()}
                  </p>

                  <p>
                    <strong>Failure Type:</strong>{" "}
                    {searchResult.audit.lastFailedLogin.event_type}
                  </p>

                  <p>
                    <strong>Failed Login IP:</strong>{" "}
                    {searchResult.audit.lastFailedLogin.ip_address ||
                      "Not available"}
                  </p>
                </>
              ) : (
                <p>No failed login audit found.</p>
              )}
            </div>
            <div style={{ marginTop: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Change User Group
              </label>

              <select
                value={searchResult.group_id || ""}
                onChange={(e) =>
                  setSearchResult({
                    ...searchResult,
                    group_id: Number(e.target.value),
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  marginBottom: "12px",
                }}
              >
                {[
                  { id: 1, name: "Admin" },
                  { id: 2, name: "Moderation" },
                  { id: 3, name: "Tech" },
                  { id: 4, name: "Support" },
                  ...cloud9Groups,
                  { id: 10, name: "Reserved" },
                  { id: 11, name: "Blocked" },
                ].map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.id})
                  </option>
                ))}
              </select>

              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Change @${searchResult.username} to group ${searchResult.group_id}?`
                    )
                  ) {
                    return;
                  }

                  try {
                    const res = await fetch(
                      `${process.env.REACT_APP_API_URL}/admin/update-group`,
                      {
                        method: "PATCH",
                        credentials: "include",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          user_id: searchResult.id,
                          group_id: searchResult.group_id,
                        }),
                      }
                    );

                    const data = await res.json();

                    if (!data.success) {
                      alert(`❌ ${data.error || "Failed to update group."}`);
                      return;
                    }

                    alert("✅ User group updated successfully.");
                  } catch {
                    alert("❌ Network error updating group.");
                  }
                }}
              >
                💎 Update Group
              </button>
            </div>

            <button
              style={{ marginTop: "14px" }}
              onClick={() => setSearchResult(null)}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* BOOST */}
      <div className="admin-section">
        <h3>Boost & Post Management</h3>
        <div className="admin-grid">
          <button onClick={handleAdminBoost}>🌟 Admin Boost</button>
          <button onClick={handleBoostQueueRotation}>♻️ Rotate Queue</button>
          <button onClick={() => setShowMatrixModal(true)}>
            🌤️ Manage Group Settings
          </button>
        </div>

        <div className="admin-search">
          <input
            type="text"
            placeholder="Enter username to find posts..."
            value={postSearchUsername}
            onChange={(e) => {
              setPostSearchUsername(e.target.value);
              if (!e.target.value.trim()) {
                setUserPosts([]);
                setExpandedPosts({});
              }
            }}
          />
          <button onClick={handleSearchUserPosts}>
            {loadingUserPosts ? "Searching..." : "🔎 Search Posts"}
          </button>
        </div>

        {userPosts.length > 0 && (
          <div className="admin-card">
            <h4>📝 Posts Found</h4>

            {userPosts.map((post) => (
              <div key={post.id} className="admin-post-card">
                <p>
                  <strong>Post ID:</strong> {post.id}
                </p>
                <p>
                  <strong>User:</strong> @{post.username}
                </p>

                <p
                  className={
                    expandedPosts[post.id]
                      ? "admin-post-content"
                      : "admin-post-content admin-post-content-collapsed"
                  }
                >
                  {post.content || "(No text content)"}
                </p>

                {post.content && post.content.length > 220 && (
                  <button
                    type="button"
                    className="admin-post-more-button"
                    onClick={() => togglePostExpanded(post.id)}
                  >
                    {expandedPosts[post.id] ? "Less" : "More"}
                  </button>
                )}

                <p>
                  <strong>Boosted:</strong> {post.isBoosted ? "Yes" : "No"}
                </p>

                <button onClick={() => handleBoostFoundPost(post.id)}>
                  🌟 Boost This Post
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* REPORTS MODAL */}
      {showReportsModal && (
        <div className="admin-modal">
          <h3>⚠️ User Reports</h3>

          {loadingReports ? (
            <p>Loading...</p>
          ) : reports.length > 0 ? (
            <ul className="modal-list">
              {reports.map((r) => (
                <li key={r.id}>
                  <div>
                    <strong>{r.reporter_username}</strong> →{" "}
                    {r.reported_username} <br />
                    📝 <em>{r.reason}</em> | Status: {r.status}
                  </div>

                  <div>
                    <button onClick={() => viewReportedPost(r.post_id, r)}>
                      🔍 View Post
                    </button>
                    <button onClick={() => updateReportStatus(r.id, "reviewed")}>
                      ✅ Reviewed
                    </button>
                    <button onClick={() => updateReportStatus(r.id, "dismissed")}>
                      ❌ Dismiss
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>No reports found.</p>
          )}

          <button onClick={() => setShowReportsModal(false)}>Close</button>
        </div>
      )}

      {/* PASSWORD RESET MODAL */}
      {showResetModal && (
        <div className="admin-modal">
          <h3>🔐 Reset User Password</h3>

          <div className="admin-input-group">
            <label>Username:</label>
            <input
              type="text"
              value={resetUsername}
              onChange={(e) => setResetUsername(e.target.value)}
            />
          </div>

          <div className="admin-input-group">
            <label>New Password:</label>
            <input
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
            />
          </div>

          <button
            onClick={async () => {
              if (!resetUsername || !resetPassword) {
                alert("❌ Enter both username and password.");
                return;
              }

              try {
                const res = await fetch(
                  `${process.env.REACT_APP_API_URL}/admin/reset-password`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                      adminId: adminId,
                      username: resetUsername,
                      newPassword: resetPassword,
                    }),
                  }
                );

                const data = await res.json();

                if (data.error) {
                  alert(`❌ ${data.error}`);
                  return;
                }

                alert("✅ Password reset successfully.");
                setShowResetModal(false);
                setResetUsername("");
                setResetPassword("");
              } catch {
                alert("❌ Network error resetting password.");
              }
            }}
          >
            🔄 Reset Password
          </button>

          <button onClick={() => setShowResetModal(false)}>Close</button>
        </div>
      )}

      {/* POST DETAIL MODAL */}
      {showReportPostModal && reportPostDetail && (
        <div className="admin-modal">
          <h3>🧾 Reported Post Detail</h3>

          {reportPostDetail.reportContext && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: "10px" }}>
                ⚠️ Report Information
              </h4>
              <p>
                <strong>Reported by:</strong>{" "}
                {reportPostDetail.reportContext.reporter_username || "Unknown"}
              </p>
              <p>
                <strong>Reported user:</strong>{" "}
                {reportPostDetail.reportContext.reported_username || "Unknown"}
              </p>
              <p>
                <strong>Reason:</strong>{" "}
                {reportPostDetail.reportContext.reason || "Not provided"}
              </p>
              <p>
                <strong>Explanation:</strong>{" "}
                {reportPostDetail.reportContext.details || "No explanation provided."}
              </p>
              <p style={{ marginBottom: 0 }}>
                <strong>Status:</strong>{" "}
                {reportPostDetail.reportContext.status || "Unknown"}
              </p>
            </div>
          )}

          <p>
            <strong>Post ID:</strong> {reportPostDetail.id}
          </p>
          <p>
            <strong>Author:</strong> {reportPostDetail.username || "Unknown"}
          </p>
          <p>
            <strong>Posted:</strong>{" "}
            {new Date(reportPostDetail.createdAt).toLocaleString()}
          </p>

          <p>
            <strong>Content:</strong>
          </p>
          <div className="post-preview-box">
            {reportPostDetail.content || "(No text content)"}
          </div>

          {reportPostDetail.media_url && (
            <div style={{ marginTop: "10px" }}>
              {reportPostDetail.media_url.match(/\.(mp4|mov|webm)$/i) ? (
                <video
                  controls
                  style={{ maxWidth: "100%", borderRadius: "6px" }}
                >
                  <source
                    src={`${process.env.REACT_APP_API_URL}${reportPostDetail.media_url}`}
                  />
                </video>
              ) : (
                <img
                  src={`${process.env.REACT_APP_API_URL}${reportPostDetail.media_url}`}
                  alt="attached"
                  style={{ maxWidth: "100%", borderRadius: "6px" }}
                />
              )}
            </div>
          )}

          {reportPostDetail.gif_url && (
            <div style={{ marginTop: "10px" }}>
              <img
                src={reportPostDetail.gif_url}
                alt="gif"
                style={{ maxWidth: "100%", borderRadius: "6px" }}
              />
            </div>
          )}

          <button
            style={{
              backgroundColor: "#b30000",
              color: "white",
              marginTop: "12px",
              marginRight: "10px",
            }}
            onClick={async () => {
              if (!window.confirm("⚠️ Are you sure you want to delete this post?"))
                return;

              try {
                const url = `${process.env.REACT_APP_API_URL}/posts/${reportPostDetail.id}?username=admin&group_id=1`;

                const res = await fetch(url, {
                  method: "DELETE",
                  credentials: "include",
                });

                const data = await res.json();

                if (data.error) {
                  alert(`❌ ${data.error}`);
                  return;
                }

                alert("🗑️ Post deleted successfully.");

                setShowReportPostModal(false);
                fetchReports();
              } catch (err) {
                alert("❌ Network error deleting post.");
              }
            }}
          >
            🗑️ Delete Post
          </button>

          <button onClick={() => setShowReportPostModal(false)}>Close</button>
        </div>
      )}

      {/* MUTED USERS */}
      {showMutedModal && (
        <div className="admin-modal">
          <h3>👥 Muted Users</h3>
          {mutedUsers.length > 0 ? (
            <ul className="modal-list">
              {mutedUsers.map((m) => (
                <li key={m.id}>
                  <span>
                    {m.muter_username} → {m.muted_username}
                  </span>
                  <button onClick={() => removeMute(m.id)}>🗑 Remove</button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No muted users found.</p>
          )}
          <button onClick={() => setShowMutedModal(false)}>Close</button>
        </div>
      )}

      {/* BLOCKED USERS */}
      {showBlockedModal && (
        <div className="admin-modal">
          <h3>🚫 Blocked Users</h3>
          {blockedUsers.length > 0 ? (
            <ul className="modal-list">
              {blockedUsers.map((b) => (
                <li key={b.id}>
                  <span>
                    {b.blocker_username} → {b.blocked_username}
                  </span>
                  <button onClick={() => removeBlock(b.id)}>🗑 Remove</button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No blocked users found.</p>
          )}
          <button onClick={() => setShowBlockedModal(false)}>Close</button>
        </div>
      )}

      {/* Administrative moderation reports — read-only */}
      {adminBlocksReport !== null && (
        <div className="admin-modal" style={{ maxWidth: "100%", overflowX: "auto" }}>
          <h3>Administrative Blocks — Current Group 11</h3>
          <table><thead><tr><th>Username</th><th>User ID</th><th>Previous Group</th><th>Suspended</th><th>Admin ID</th><th>Details</th></tr></thead>
            <tbody>{adminBlocksReport.map(u => <tr key={u.id}><td>{u.username}</td><td>{u.id}</td><td>{u.previous_group_id ?? "Unavailable"}</td><td>{u.suspended_at ? new Date(u.suspended_at).toLocaleString() : "Unavailable"}</td><td>{u.administrator_id ?? "Unavailable"}</td><td>{u.details || "Unavailable"}</td></tr>)}</tbody></table>
          {adminBlocksReport.length === 0 && <p>No Group 11 accounts found.</p>}
          <button onClick={() => setAdminBlocksReport(null)}>Close</button>
        </div>
      )}
      {warningHistoryReport !== null && (
        <div className="admin-modal" style={{ maxWidth: "100%", overflowX: "auto" }}>
          <h3>Warning History</h3>
          <p>Counts reflect retained warning records. Reinstatement removes the latest warning.</p>
          <table><thead><tr><th>Username</th><th>User ID</th><th>Reason</th><th>Date</th><th>Administrator</th><th>Cumulative Count</th></tr></thead>
            <tbody>{warningHistoryReport.map(w => <tr key={w.id}><td>{w.username || "Unavailable"}</td><td>{w.user_id}</td><td>{w.reason}</td><td>{w.created_at ? new Date(w.created_at).toLocaleString() : "Unavailable"}</td><td>{w.administrator || `ID ${w.admin_id}`}</td><td>{w.cumulative_count}</td></tr>)}</tbody></table>
          {warningHistoryReport.length === 0 && <p>No warning records found.</p>}
          <button onClick={() => setWarningHistoryReport(null)}>Close</button>
        </div>
      )}

      {/* V9.7.0 REGISTRATION ANALYTICS — read-only */}
      {showRegistrationAnalytics && registrationAnalytics && (
        <div className="admin-modal" style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <h3>Registration Analytics</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "12px" }}>
            <span><strong>Current:</strong> {registrationAnalytics.summary.currentAccounts}</span>
            <span><strong>Verified:</strong> {registrationAnalytics.summary.verified}</span>
            <span><strong>Unverified:</strong> {registrationAnalytics.summary.unverified}</span>
            <span><strong>Historical Deleted (since 9.7.0):</strong> {registrationAnalytics.summary.historicalDeleted}</span>
          </div>
          <p>Historical registration auditing begins with this release. Earlier deleted accounts cannot be reconstructed from the current Users table.</p>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "12px" }}>
            <input aria-label="Search registrations" placeholder="Search username or email" value={registrationFilter} onChange={(e) => setRegistrationFilter(e.target.value)} />
            <select aria-label="Verification status" value={registrationStatus} onChange={(e) => setRegistrationStatus(e.target.value)}>
              <option value="ALL">All statuses</option>
              <option value="VERIFIED">Verified</option>
              <option value="UNVERIFIED">Unverified</option>
              <option value="DELETED">Historical deleted</option>
            </select>
          </div>
          <div style={{ maxHeight: "520px", overflowY: "auto", overflowX: "auto" }}>
            {[
              ...(registrationStatus === "DELETED" ? [] : registrationAnalytics.accounts.map((u) => ({ ...u, status: u.email_verified ? "Verified" : "Unverified", registeredAt: u.createdAt }))),
              ...(registrationStatus === "ALL" || registrationStatus === "DELETED" ? registrationAnalytics.historicalDeleted.map((u) => ({ ...u, status: "Deleted (historical)", registeredAt: u.createdAt })) : []),
            ].filter((u) => {
              const q = registrationFilter.toLowerCase().trim();
              if (q && !`${u.username || ""} ${u.email || ""}`.toLowerCase().includes(q)) return false;
              if (registrationStatus === "VERIFIED" && u.status !== "Verified") return false;
              if (registrationStatus === "UNVERIFIED" && u.status !== "Unverified") return false;
              return true;
            }).map((u) => (
              <div key={`${u.status}-${u.id}`} style={{ padding: "9px 2px", borderBottom: "1px solid #999", overflowWrap: "anywhere" }}>
                <strong>{u.username || "Unknown"}</strong> — {u.status}<br />
                {u.email || "No email"}<br />
                Registered: {u.registeredAt ? new Date(u.registeredAt).toLocaleString() : "Unknown"}
              </div>
            ))}
          </div>
          <button style={{ marginTop: "14px" }} onClick={() => setShowRegistrationAnalytics(false)}>Close</button>
        </div>
      )}

               {/* USER LOGIN ANALYTICS */}
      {showUserLoginAnalyticsModal && (
        <div
          className="admin-modal"
          style={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflowX: "hidden",
          }}
        >
          <h3>User Login Analytics</h3>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
            <input aria-label="Login search" placeholder="Username, email or IP" value={loginSearch} onChange={(e) => setLoginSearch(e.target.value)} />
            <select aria-label="Login status" value={loginStatus} onChange={(e) => setLoginStatus(e.target.value)}>
              <option value="ALL">All events</option><option value="SUCCESS">Successful</option>
              <option value="FAILED">Failed / Denied</option><option value="GUEST">Historical Guest</option>
            </select>
            <input aria-label="Login from date" type="date" value={loginFrom} onChange={(e) => setLoginFrom(e.target.value)} />
            <input aria-label="Login to date" type="date" value={loginTo} onChange={(e) => setLoginTo(e.target.value)} />
            <input aria-label="Login country" placeholder="Country (all history)" value={loginCountry} onChange={(e) => setLoginCountry(e.target.value)} />
            <button onClick={() => handleUserLoginAnalytics(1)} disabled={loadingUserLoginAnalytics}>Apply Filters</button>
          </div>
          <p style={{ fontSize: "12px" }}>Country is an approximate IP location. Country, date, status and username filters search all recorded login events. Dates use UTC.</p>

          {loadingUserLoginAnalytics ? (
            <p>Loading...</p>
          ) : userLoginAnalytics?.events?.length > 0 ? (
            <>
              <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "2px solid #ccc" }}>
                <h4 style={{ margin: "0 0 8px" }}>Overall Login History — Matching Filters</h4>
                <p style={{ margin: "0 0 6px" }}><strong>Total Events:</strong> {userLoginAnalytics.total ?? 0}</p>
                <p style={{ margin: "0 0 6px" }}><strong style={{ color: "green" }}>● Successful Logins:</strong> {userLoginAnalytics.summary?.successfulLogins ?? "—"}</p>
                <p style={{ margin: "0 0 6px" }}><strong style={{ color: "#0066cc" }}>● Historical Guest Sessions:</strong> {userLoginAnalytics.summary?.guestSessions ?? "—"}</p>
                <p style={{ margin: "0 0 6px" }}><strong style={{ color: "red" }}>● Failed or Denied:</strong> {userLoginAnalytics.summary?.failedOrDenied ?? "—"}</p>
                <h4 style={{ margin: "12px 0 5px" }}>Current Page — {loginPage} of {userLoginAnalytics.totalPages || 1}</h4>
                <p style={{ margin: 0 }}>Showing {userLoginAnalytics.events.length} login events on this page.</p>
              </div>

              <div
                style={{
                  maxHeight: "520px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                {userLoginAnalytics.events.map((event) => {
                  const isGuest = event.event_type === "LOGIN_GUEST";
                  const isSuccessful =
                    event.event_type === "LOGIN_SUCCESS" &&
                    event.event_status === "SUCCESS";

                  const statusColor = isGuest
                    ? "#0066cc"
                    : isSuccessful
                    ? "green"
                    : "red";

                  const statusLabel = isGuest
                    ? "GUEST"
                    : event.event_status || "UNKNOWN";

                  return (
                    <div
                      key={event.id}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "12px 2px",
                        borderBottom: "1px solid #ccc",
                        overflowWrap: "anywhere",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          marginBottom: "7px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: "11px",
                              height: "11px",
                              borderRadius: "50%",
                              backgroundColor: statusColor,
                              flexShrink: 0,
                            }}
                          />

                          <strong style={{ color: statusColor }}>
                            {statusLabel}
                          </strong>
                        </div>

                        <strong>
                          {event.createdAt
                            ? new Date(event.createdAt).toLocaleString()
                            : "Date unknown"}
                        </strong>
                      </div>

                      <p style={{ margin: "0 0 5px 0" }}>
                        <strong>User:</strong>{" "}
                        {isGuest
                          ? "Guest Session"
                          : event.username
                          ? `@${event.username}`
                          : "Unknown user"}

                        {!isGuest && event.user_id
                          ? ` (ID ${event.user_id})`
                          : ""}
                      </p>

                      <p style={{ margin: "0 0 5px 0" }}>
                        <strong>IP:</strong>{" "}
                        {event.ip_address || "None recorded"}
                      </p>

                      <p style={{ margin: "0 0 5px 0" }}><strong>Country:</strong> {event.country || "Unknown"}</p>
                      <p style={{ margin: 0 }}>
                        <strong>Event:</strong>{" "}
                        {event.event_type || "Unknown"}
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p>No login audit records found.</p>
          )}

          <div style={{ display: "flex", gap: "8px", marginTop: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" disabled={loadingUserLoginAnalytics || loginPage <= 1} onClick={() => handleUserLoginAnalytics(1)}>First</button>
            <button type="button" disabled={loadingUserLoginAnalytics || loginPage <= 1} onClick={() => handleUserLoginAnalytics(loginPage - 1)}>Previous</button>
            <span>Page {loginPage} / {userLoginAnalytics?.totalPages || 1}</span>
            <button type="button" disabled={loadingUserLoginAnalytics || loginPage >= (userLoginAnalytics?.totalPages || 1)} onClick={() => handleUserLoginAnalytics(loginPage + 1)}>Next</button>
            <button type="button" disabled={loadingUserLoginAnalytics || loginPage >= (userLoginAnalytics?.totalPages || 1)} onClick={() => handleUserLoginAnalytics(userLoginAnalytics.totalPages)}>Last</button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const page = Number(loginJumpPage);
              const lastPage = userLoginAnalytics?.totalPages || 1;
              if (!Number.isInteger(page) || page < 1 || page > lastPage) {
                alert(`Enter a page number between 1 and ${lastPage}.`);
                return;
              }
              handleUserLoginAnalytics(page);
            }}
            style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "center", flexWrap: "wrap" }}
          >
            <label htmlFor="login-jump-page">Go to page:</label>
            <input id="login-jump-page" type="number" min="1" max={userLoginAnalytics?.totalPages || 1}
              value={loginJumpPage} onChange={(e) => setLoginJumpPage(e.target.value)}
              style={{ width: "75px" }} disabled={loadingUserLoginAnalytics} />
            <button type="submit" disabled={loadingUserLoginAnalytics}>Go</button>
          </form>
          <button
            style={{ marginTop: "14px" }}
            onClick={() => setShowUserLoginAnalyticsModal(false)}
          >
            Close
          </button>
        </div>
      )}           

      {/* VIEW ANALYTICS SUMMARY */}
      {showViewSummaryModal && viewSummary && (
        <div className="admin-modal">
          <h3>📊 View Analytics Summary</h3>

          {loadingViewSummary ? (
            <p>Loading...</p>
          ) : (
            <>
              <p>
                <strong>Views Logged Today:</strong>{" "}
                {viewSummary.rowsToday ?? 0}
              </p>
              <p>
                <strong>Total Analytics Rows:</strong>{" "}
                {viewSummary.totalRows ?? 0}
              </p>

              <h4>🔥 Top Viewed Posts Today</h4>
              {viewSummary.topPostsToday?.length > 0 ? (
                <ul className="modal-list">
                  {viewSummary.topPostsToday.map((p, idx) => (
                    <li key={`today-${idx}`}>
                      <span>
                        Post ID {p.post_id} — {p.views_today} views
                        {p.username ? ` — @${p.username}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No post views found today.</p>
              )}

              <h4>👀 Top Viewers Today</h4>
              {viewSummary.topViewersToday?.length > 0 ? (
                <ul className="modal-list">
                  {viewSummary.topViewersToday.map((u, idx) => (
                    <li key={`viewer-${idx}`}>
                      <span>User ID {u.user_id} — {u.views_today} views</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No viewer activity found today.</p>
              )}

              <h4>⏱ Top Posts Last Hour</h4>
              {viewSummary.topPostsLastHour?.length > 0 ? (
                <ul className="modal-list">
                  {viewSummary.topPostsLastHour.map((p, idx) => (
                    <li key={`hour-${idx}`}>
                      <span>
                        Post ID {p.post_id} — {p.views_last_hour} views
                        {p.username ? ` — @${p.username}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No post views found in the last hour.</p>
              )}
            </>
          )}

          <button onClick={() => setShowViewSummaryModal(false)}>Close</button>
        </div>
      )}

      {/* ORPHANED FILES */}
      {orphaned && (
        <div className="admin-modal">
          <h3>🧹 Orphaned Uploads</h3>

          {orphaned.error ? (
            <p className="error">❌ {orphaned.error}</p>
          ) : (
            <>
              <p>
                <strong>Found:</strong> {orphaned.total} orphaned file(s)
              </p>

              <p>
                <strong>Selected:</strong> {selectedOrphans.length}
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  marginBottom: "12px",
                }}
              >
                <button
                  type="button"
                  onClick={selectAllOrphans}
                  disabled={!orphaned.orphaned?.length || deletingOrphans}
                >
                  Select All
                </button>

                <button
                  type="button"
                  onClick={clearOrphanSelection}
                  disabled={selectedOrphans.length === 0 || deletingOrphans}
                >
                  Clear Selection
                </button>

                <button
                  type="button"
                  onClick={handleDeleteSelectedOrphans}
                  disabled={selectedOrphans.length === 0 || deletingOrphans}
                  style={{
                    backgroundColor: "#b30000",
                    color: "white",
                  }}
                >
                  {deletingOrphans
                    ? "Deleting..."
                    : `Delete Selected (${selectedOrphans.length})`}
                </button>
              </div>

              <ul className="modal-list">
                {orphaned.orphaned.map((file) => (
                  <li key={file}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "100%",
                        overflowWrap: "anywhere",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrphans.includes(file)}
                        onChange={() => toggleOrphanSelection(file)}
                        disabled={deletingOrphans}
                      />
                      <span>{file}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}

          <button
            onClick={() => {
              setOrphaned(null);
              setSelectedOrphans([]);
            }}
            disabled={deletingOrphans}
          >
            Close
          </button>
        </div>
      )}

      {showMatrixModal && (
        <Cloud9MatrixModal
          open={showMatrixModal}
          onClose={() => setShowMatrixModal(false)}
          groupData={cloud9Groups}
        />
      )}
    </div>
  );
};

export default AdminTools;